import Axios from '../../axiosConfig';
import { BACKEND_URL } from '../../config';
import { configure, runInAction, makeAutoObservable } from 'mobx';
import {
    OrganizationCategory,
    OrganizationDto,
    GetOrganizationsWithLicensesRequest,
    SaveOrganizationRequest,
    CallUserToOrganizationRequest,
} from '../../types/organization';
import RootModule from '../rootModule';

configure({ enforceActions: 'observed' });

export default class OrganizationModule {
    private rootModule: RootModule;
    private orgLocations: OrganizationDto[] = [];
    private categories: OrganizationCategory[] = [];
    private availableCollectionCodes: number[] = [];
    private adminOrganizationList: OrganizationDto[] = [];

    constructor(rootModule: RootModule) {
        makeAutoObservable(this, {}, { autoBind: true });
        this.rootModule = rootModule;
    }

    public setAdminOrganizationList(organizationsList: OrganizationDto[]) {
        this.adminOrganizationList = organizationsList;
    }

    public async fetchOrganizationLocations(): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + 'organization/allOrganizations',
            withCredentials: true,
        });
        if (response?.status === 200) runInAction(() => (this.orgLocations = response.data));
    }

    public async fetchOrganizationCategories(): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + 'organization/organizationCategories',
            withCredentials: true,
        });
        runInAction(() => (this.categories = response.data));
    }

    public async fetchOrganizationsWithLicenses(
        request: GetOrganizationsWithLicensesRequest,
    ): Promise<OrganizationDto[]> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'organization/getOrganizationsWithLicenses',
            data: request,
            withCredentials: true,
        });
        return response.data;
    }

    public async fetchCollectionOrganizations(organizationId: number): Promise<OrganizationDto[]> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + `organization/getCollectionOrganizations/${organizationId}`,
            withCredentials: true,
        });
        return response.data;
    }

    public async fetchAvailableCollectionCodes(): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + 'organization/availableCollectionCodes',
            withCredentials: true,
        });
        runInAction(() => (this.availableCollectionCodes = response.data));
    }

    public async fetchAllOrganizations(): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + 'organization/allOrganizations',
            withCredentials: true,
        });
        runInAction(() => (this.adminOrganizationList = response.data));
    }

    public async fetchAdminOrganizations(): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + 'organizationUser/adminOrganizations',
            withCredentials: true,
        });
        runInAction(() => (this.adminOrganizationList = response.data));
    }

    public async callUserToOrganization(requestData: CallUserToOrganizationRequest): Promise<void> {
        await Axios({
            method: 'POST',
            url: BACKEND_URL + 'organizationUser/callUserToOrganization',
            data: requestData,
            withCredentials: true,
        });
    }

    public async saveOrganization(organization: SaveOrganizationRequest): Promise<OrganizationDto> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'organization/saveOrganization',
            data: organization,
            withCredentials: true,
        });
        return response.data;
    }

    public async searchOrganizations(searchString: string): Promise<OrganizationDto[]> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'organization/search',
            data: { value: searchString },
            withCredentials: true,
        });
        return response.data;
    }

    public get organizationLocations(): OrganizationDto[] {
        const { locale } = this.rootModule.localization;
        return this.orgLocations
            .filter((org) => org.longitude)
            .slice()
            .sort((a, b) => {
                const aName = a.name[locale] || a.name.fi_FI || a.name.sv_SE || a.name.en_GB;
                const bName = b.name[locale] || b.name.fi_FI || b.name.sv_SE || b.name.en_GB;
                return aName.localeCompare(bName);
            });
    }

    public get organizationCategories(): OrganizationCategory[] {
        return this.categories;
    }

    public get collectionCodes(): number[] {
        return this.availableCollectionCodes;
    }

    public get adminOrganizations(): OrganizationDto[] {
        return this.adminOrganizationList;
    }

    public get sortedAdminOrganizations(): OrganizationDto[] {
        //sorted by name and category
        return this.adminOrganizationList
            .slice()
            .sort((a, b) => (a.name < b.name ? 1 : -1))
            .sort((a, b) => (a.category > b.category ? 1 : -1));
    }

    public getOrganizationCategoryByName(name: string): OrganizationCategory | null {
        return this.organizationCategories.find((category) => category.name === name) || null;
    }

    // When user logs in -> selected organization is set to null -> home organization is the active organization.
    // When user changes organization -> selected organization is the active organization.
    // This getter can be used to get the active organization id.
    public get currentOrganizationId(): number | null {
        const selectedOrganizationId = this.rootModule.user.userDetails?.selectedOrganization?.id;
        const homeOrganizationId = this.rootModule.user.userDetails?.homeOrganization.id;
        return selectedOrganizationId || homeOrganizationId || null;
    }

    public get currentOrganization(): OrganizationDto | null {
        const selectedOrganization = this.rootModule.user.userDetails?.selectedOrganization;
        const homeOrganization = this.rootModule.user.userDetails?.homeOrganization;
        return selectedOrganization || homeOrganization || null;
    }
}
